<template>
  <div id="app">
    <router-view/>
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style>
:root {
  --primary-color: #2563eb;
  --primary-hover: #1d4ed8;
  --bg-page: #f8fafc;
  --bg-card: #ffffff;
  --text-main: #1e293b;
  --text-muted: #64748b;
  --border-color: #e2e8f0;
  --border-radius-base: 8px;
  --border-radius-card: 12px;
  --shadow-base: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.02);
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  --bg-header: rgba(255, 255, 255, 0.8);
  --bg-sidebar: #ffffff;
  --bg-hero: linear-gradient(105deg, #ffffff 10%, #f8fafc 40%, #f0f7ff 75%, #e0f2fe 100%);

  /* 适配新增变量 */
  --bg-hover: #f1f5f9;
  --bg-hover-deep: #e2e8f0;
  --border-light: #f1f5f9;
  --border-hover: #cbd5e1;

  /* 重载/警告按钮相关颜色 */
  --bg-reload: #fff7ed;
  --border-reload: #fed7aa;
  --text-reload: #c2410c;
  --bg-reload-hover: #ffedd5;
  --border-reload-hover: #fdba74;
  --text-reload-hover: #9a3412;

  /* 全局搜索与状态遮罩 */
  --bg-search-overlay: rgba(15, 23, 42, 0.22);
  --border-search-input: #dbeafe;
  --bg-tip-hover: #eff6ff;
  --border-tip-hover: #bfdbfe;
  --text-tip-hover: #1d4ed8;
  --bg-jump-badge: #dbeafe;
  --text-jump-badge: #2563eb;
  --bg-overlay: rgba(255, 255, 255, 0.95);
}

html.dark {
  --bg-page: #0f172a;
  --bg-card: #1e293b;
  --text-main: #e2e8f0;
  --text-muted: #94a3b8;
  --border-color: #334155;
  --bg-header: rgba(30, 41, 59, 0.8);
  --bg-sidebar: #1e293b;
  --bg-hero: linear-gradient(105deg, #1e293b 10%, #0f172a 40%, #1e3a5f 75%, #0f2b48 100%);
  --shadow-base: 0 1px 3px 0 rgba(0, 0, 0, 0.5), 0 4px 6px -1px rgba(0, 0, 0, 0.5);
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.3);

  /* 适配新增暗色变量 */
  --bg-hover: #334155;
  --bg-hover-deep: #475569;
  --border-light: rgba(255, 255, 255, 0.06);
  --border-hover: #475569;

  /* 重载/警告按钮暗色映射 */
  --bg-reload: rgba(245, 158, 11, 0.1);
  --border-reload: rgba(245, 158, 11, 0.2);
  --text-reload: #f59e0b;
  --bg-reload-hover: rgba(245, 158, 11, 0.2);
  --border-reload-hover: rgba(245, 158, 11, 0.3);
  --text-reload-hover: #fbbf24;

  /* 全局搜索与状态遮罩暗色映射 */
  --bg-search-overlay: rgba(15, 23, 42, 0.65);
  --border-search-input: rgba(37, 99, 235, 0.2);
  --bg-tip-hover: rgba(59, 130, 246, 0.1);
  --border-tip-hover: rgba(59, 130, 246, 0.25);
  --text-tip-hover: #3b82f6;
  --bg-jump-badge: rgba(37, 99, 235, 0.25);
  --text-jump-badge: #60a5fa;
  --bg-overlay: rgba(30, 41, 59, 0.95);
}

/* 全局重置与字体 */
#app {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-main);
  height: 100%;
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  background-color: var(--bg-page);
}

/* 全局滚动条美化 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
html.dark ::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
}
::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.24);
}
html.dark ::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.24);
}

/* 全局公用卡片优化 */
.box {
  margin-top: 12px;
  background: var(--bg-card);
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.01), 0 1px 2px 0 rgba(0, 0, 0, 0.02) !important;
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
}

.box:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
  border-color: rgba(0, 0, 0, 0.08);
}
html.dark .box:hover {
  border-color: var(--border-color);
}

/* Element UI 全局精致样式微调 */
.el-button {
  border-radius: 6px !important;
  font-weight: 500 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.el-button--primary {
  background-color: #3b82f6 !important;
  border-color: #3b82f6 !important;
}

.el-button--primary:hover {
  background-color: #2563eb !important;
  border-color: #2563eb !important;
}

.el-input__inner {
  border-radius: 6px !important;
}

.el-dialog {
  border-radius: 10px !important;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.el-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f4f4f5;
  background-color: var(--bg-card) !important;
}

.el-table tr {
  background-color: var(--bg-card) !important;
}

.el-table th {
  background-color: #f8f9fa !important;
  color: #52525b !important;
  font-weight: 600 !important;
}

/* 统一亮色模式下的表格行背景、斑马纹及 hover 色值 */
.el-table--striped .el-table__row--striped td {
  background-color: var(--bg-page) !important;
}
.el-table--enable-row-hover .el-table__row:hover > td {
  background-color: var(--bg-hover) !important;
}

/* 消除固定列的投影，消除视觉差异 */
.el-table__fixed,
.el-table__fixed-right {
  box-shadow: none !important;
}

/* 确保亮色下固定列与普通列在各种状态下的背景完全一致 */
.el-table__fixed .el-table__cell,
.el-table__fixed-right .el-table__cell {
  background-color: var(--bg-card) !important;
}
.el-table__fixed-header-wrapper th,
.el-table__fixed-right .el-table__fixed-header-wrapper th {
  background-color: #f8f9fa !important;
}
.el-table--striped .el-table__row--striped .el-table__cell {
  background-color: var(--bg-page) !important;
}
.el-table__body tr:hover > td.el-table__cell {
  background-color: var(--bg-hover) !important;
}

.el-form--inline .el-form-item {
  margin-bottom: 8px;
}

/* 修复表格数据为空（暂无数据）时的底部边框显示问题 */
.el-table__empty-block {
  position: relative;
}

/* 对于 premium-table，空状态下在 empty-block 底部补充 #F1F5F9 细线以补齐缺失的行下划线 */
.premium-table .el-table__empty-block {
  border-bottom: 1px solid #f1f5f9 !important;
}

/* 针对常规 el-table 表格，在 empty-block 底部补充 #f4f4f5 细线，使其与外边框无缝融合 */
.el-table:not(.premium-table) .el-table__empty-block {
  border-bottom: 1px solid #f4f4f5 !important;
}

/* 隐藏数据为空或表格滚动时固定列容器底部产生的多余/割裂小短线 */
.el-table__fixed::before,
.el-table__fixed-right::before {
  display: none !important;
}

/* ==========================================================================
   Element UI 暗色模式样式适配覆盖
   ========================================================================== */
html.dark body {
  background-color: var(--bg-page) !important;
  color: var(--text-main) !important;
}

/* 1. 输入框 / 选择器 / 文本域 */
html.dark .el-input__inner,
html.dark .el-textarea__inner {
  background-color: #0f172a !important;
  border-color: #334155 !important;
  color: var(--text-main) !important;
}
html.dark .el-input__inner::placeholder,
html.dark .el-textarea__inner::placeholder {
  color: #475569 !important;
}
html.dark .el-input__inner:focus,
html.dark .el-textarea__inner:focus {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25) !important;
}
html.dark .el-input.is-disabled .el-input__inner {
  background-color: #0c1220 !important;
  border-color: #1e293b !important;
  color: #475569 !important;
}

/* 2. 下拉菜单 / 弹出选择框 */
html.dark .el-select-dropdown,
html.dark .el-dropdown-menu,
html.dark .el-picker-panel,
html.dark .el-autocomplete-suggestion {
  background-color: #1e293b !important;
  border: 1px solid #334155 !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3) !important;
}
html.dark .el-select-dropdown__item {
  color: #94a3b8 !important;
}
html.dark .el-select-dropdown__item.hover,
html.dark .el-select-dropdown__item:hover,
html.dark .el-dropdown-menu__item:hover,
html.dark .el-autocomplete-suggestion li:hover {
  background-color: #334155 !important;
  color: #f1f5f9 !important;
}
html.dark .el-select-dropdown__item.selected {
  color: #3b82f6 !important;
  font-weight: 600;
}
html.dark .el-dropdown-menu__item {
  color: #94a3b8 !important;
}
html.dark .el-dropdown-menu__item.is-disabled {
  color: #475569 !important;
}
html.dark .el-dropdown-menu__item--divided {
  border-top: 1px solid #334155 !important;
}
html.dark .el-dropdown-menu__item--divided::before {
  background-color: #1e293b !important;
}
html.dark .el-popper[x-placement^=bottom] .popper__arrow::after {
  border-bottom-color: #1e293b !important;
}
html.dark .el-popper[x-placement^=bottom] .popper__arrow {
  border-bottom-color: #334155 !important;
}
html.dark .el-popper[x-placement^=top] .popper__arrow::after {
  border-top-color: #1e293b !important;
}
html.dark .el-popper[x-placement^=top] .popper__arrow {
  border-top-color: #334155 !important;
}

/* 3. 对话框 / 消息框 */
html.dark .el-dialog {
  background-color: #1e293b !important;
  border: 1px solid #334155 !important;
}
html.dark .el-dialog__title {
  color: #f1f5f9 !important;
}
html.dark .el-dialog__body {
  color: #e2e8f0 !important;
}
html.dark .el-message-box {
  background-color: #1e293b !important;
  border: 1px solid #334155 !important;
}
html.dark .el-message-box__title,
html.dark .el-message-box__content {
  color: #f1f5f9 !important;
}

/* 4. 表格 */
html.dark .el-table {
  background-color: var(--bg-card) !important;
  color: var(--text-main) !important;
  border-color: var(--border-color) !important;
}
html.dark .el-table th,
html.dark .el-table tr {
  background-color: var(--bg-card) !important;
  color: var(--text-main) !important;
}
html.dark .el-table th {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .el-table td,
html.dark .el-table th.is-leaf {
  border-bottom: 1px solid var(--border-light) !important;
}
html.dark .el-table--striped .el-table__row--striped td {
  background: rgba(15, 23, 42, 0.45) !important;
}
html.dark .el-table--enable-row-hover .el-table__row:hover > td {
  background-color: var(--bg-hover) !important;
}
html.dark .el-table__empty-block {
  background-color: var(--bg-card) !important;
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .el-table__empty-text {
  color: var(--text-muted) !important;
}
html.dark .el-table::before {
  background-color: var(--border-color) !important;
}

/* 5. 分页 */
html.dark .el-pagination {
  color: #94a3b8 !important;
}
html.dark .el-pagination button:disabled {
  background-color: #1e293b !important;
  color: #475569 !important;
}
html.dark .el-pagination .btn-next,
html.dark .el-pagination .btn-prev {
  background-color: #1e293b !important;
  color: #94a3b8 !important;
}
html.dark .el-pager li {
  background-color: #1e293b !important;
  color: #94a3b8 !important;
}
html.dark .el-pager li.active {
  color: #3b82f6 !important;
}
html.dark .el-pagination__total,
html.dark .el-pagination__jump {
  color: #94a3b8 !important;
}

/* 6. 表单标签与面板 */
html.dark .el-form-item__label {
  color: #e2e8f0 !important;
}
html.dark .el-card {
  background-color: #1e293b !important;
  border: 1px solid #334155 !important;
  color: #f1f5f9 !important;
}
html.dark .el-card__header {
  border-bottom: 1px solid #334155 !important;
}

/* 7. 按钮其他样式 */
html.dark .el-button--default {
  background-color: #273549 !important;
  border-color: #3d4f68 !important;
  color: #e2e8f0 !important;
}
html.dark .el-button--default:hover {
  background-color: #33455e !important;
  color: #ffffff !important;
  border-color: #4b6180 !important;
}

/* 8. 选项卡 Tab */
html.dark .el-tabs__item {
  color: #94a3b8 !important;
}
html.dark .el-tabs__item.is-active {
  color: #3b82f6 !important;
}
html.dark .el-tabs--card > .el-tabs__header {
  border-bottom: 1px solid #334155 !important;
}
html.dark .el-tabs--card > .el-tabs__header .el-tabs__nav {
  border: 1px solid #334155 !important;
}
html.dark .el-tabs--card > .el-tabs__header .el-tabs__item {
  border-left: 1px solid #334155 !important;
}
html.dark .el-tabs--card > .el-tabs__header .el-tabs__item.is-active {
  background-color: #1e293b !important;
  border-bottom-color: #1e293b !important;
}

/* 9. 加载层 */
html.dark .el-loading-mask {
  background-color: rgba(15, 23, 42, 0.7) !important;
}

/* ==========================================================================
   二次迭代：修复已知的 7 个深色模式问题全局样式覆盖
   ========================================================================== */

/* 10. 面包屑暗色美化 */
html.dark .el-breadcrumb__inner,
html.dark .el-breadcrumb__inner a {
  color: var(--text-muted) !important;
  font-weight: 500 !important;
  transition: color 0.2s ease;
}
html.dark .el-breadcrumb__inner a:hover,
html.dark .el-breadcrumb__inner.is-link:hover {
  color: var(--primary-color) !important;
}
html.dark .el-breadcrumb__item:last-child .el-breadcrumb__inner {
  color: var(--text-main) !important;
  font-weight: 600 !important;
}
html.dark .el-breadcrumb__separator {
  color: var(--text-muted) !important;
}

/* 11. 自定义下拉菜单与上下文菜单适配 */
html.dark .premium-dropdown-menu,
html.dark .el-dropdown-menu.premium-dropdown-menu {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
}
html.dark .premium-dropdown-menu .dropdown-user-header {
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .premium-dropdown-menu .header-username {
  color: var(--text-main) !important;
}
html.dark .premium-dropdown-menu .header-role,
html.dark .premium-dropdown-menu .dropdown-item-icon {
  color: var(--text-muted) !important;
}
html.dark .premium-dropdown-menu .el-dropdown-menu__item:not(.menu-item-logout):hover {
  background-color: var(--bg-hover) !important;
  color: var(--text-main) !important;
}
html.dark .premium-dropdown-menu .menu-item-logout:hover {
  background-color: rgba(239, 68, 68, 0.15) !important;
}
html.dark .premium-dropdown-menu .el-dropdown-menu__item--divided {
  border-top: 1px solid var(--border-color) !important;
}
html.dark .premium-dropdown-menu .el-dropdown-menu__item--divided::before {
  background-color: var(--bg-card) !important;
}

/* 12. el-drawer 抽屉组件暗色适配 */
html.dark .el-drawer {
  background-color: var(--bg-card) !important;
  border-left: 1px solid var(--border-color) !important;
}
html.dark .el-drawer__header {
  color: var(--text-main) !important;
  border-bottom: 1px solid var(--border-color) !important;
  padding: 16px 20px !important;
  margin-bottom: 0 !important;
}
html.dark .el-drawer__body {
  background-color: var(--bg-card) !important;
  color: var(--text-muted) !important;
}

/* 13. el-table 表格暗色细节完美化覆盖（解决手写表格没有深色以及白色分割线问题） */
html.dark .el-table::after,
html.dark .el-table::before {
  background-color: var(--border-color) !important;
}
html.dark .el-table__row {
  background-color: var(--bg-card) !important;
}
html.dark .el-table__cell {
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .el-table th.el-table__cell {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .el-table:not(.premium-table) .el-table__empty-block {
  background-color: var(--bg-card) !important;
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .el-table__fixed,
html.dark .el-table__fixed-right {
  background-color: var(--bg-card) !important;
  box-shadow: none !important;
}
html.dark .el-table__fixed-right::before,
html.dark .el-table__fixed::before {
  background-color: var(--border-color) !important;
}
/* 覆盖固定列下的单元格背景，防止横向滚动时露底 */
html.dark .el-table__fixed .el-table__cell,
html.dark .el-table__fixed-right .el-table__cell {
  background-color: var(--bg-card) !important;
}
html.dark .el-table__fixed .el-table__body tr:hover > td.el-table__cell,
html.dark .el-table__fixed-right .el-table__body tr:hover > td.el-table__cell {
  background-color: var(--bg-hover) !important;
}

/* 覆盖固定列在暗色模式下的表头背景 */
html.dark .el-table__fixed-header-wrapper th,
html.dark .el-table__fixed-right .el-table__fixed-header-wrapper th {
  background-color: var(--bg-page) !important;
}

/* 14. 级联选择器、日期选择器等高级表单弹出层适配 */
html.dark .el-cascader__dropdown {
  background-color: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
}
html.dark .el-cascader-panel {
  background-color: var(--bg-card) !important;
}
html.dark .el-cascader-menu {
  border-right: 1px solid var(--border-color) !important;
  color: var(--text-muted) !important;
}
html.dark .el-cascader-node:not(.is-disabled):focus,
html.dark .el-cascader-node:not(.is-disabled):hover {
  background-color: var(--bg-hover) !important;
  color: var(--text-main) !important;
}
html.dark .el-cascader-node.is-active,
html.dark .el-cascader-node.is-selectable.in-active-path {
  color: var(--primary-color) !important;
  font-weight: 600 !important;
}

/* 日期范围与日历弹出面板暗色适配 */
html.dark .el-date-range-picker,
html.dark .el-date-picker {
  background-color: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--text-muted) !important;
}
html.dark .el-date-range-picker__header,
html.dark .el-date-picker__header {
  color: var(--text-main) !important;
}
html.dark .el-date-range-picker__content.is-left {
  border-right: 1px solid var(--border-color) !important;
}
html.dark .el-date-table th {
  border-bottom: 1px solid var(--border-color) !important;
  color: var(--text-muted) !important;
}
html.dark .el-date-table td.next-month,
html.dark .el-date-table td.prev-month {
  color: #475569 !important;
}
html.dark .el-date-table td.normal {
  color: var(--text-main) !important;
}
html.dark .el-date-table td.in-range div {
  background-color: rgba(59, 130, 246, 0.15) !important;
}
html.dark .el-date-table td.today span {
  color: var(--primary-color) !important;
  font-weight: 700 !important;
}
html.dark .el-date-table td.available:hover {
  color: var(--primary-color) !important;
}
html.dark .el-date-table td.current:not(.disabled) span {
  background-color: var(--primary-color) !important;
  color: #ffffff !important;
}
html.dark .el-picker-panel__footer {
  background-color: var(--bg-page) !important;
  border-top: 1px solid var(--border-color) !important;
}
html.dark .el-picker-panel__shortcut {
  color: var(--text-muted) !important;
  background-color: transparent !important;
}
html.dark .el-picker-panel__shortcut:hover {
  color: var(--primary-color) !important;
}

/* 15. 计数器 el-input-number 暗色按钮及边框 */
html.dark .el-input-number__decrease,
html.dark .el-input-number__increase {
  background-color: var(--bg-hover) !important;
  color: var(--text-muted) !important;
  border-color: var(--border-color) !important;
}
html.dark .el-input-number__decrease:hover,
html.dark .el-input-number__increase:hover {
  color: var(--text-main) !important;
  background-color: var(--bg-hover-deep) !important;
}
html.dark .el-input-number__decrease {
  border-right: 1px solid var(--border-color) !important;
}
html.dark .el-input-number__increase {
  border-left: 1px solid var(--border-color) !important;
}

/* 16. el-popover 弹出提示框 */
html.dark .el-popover {
  background-color: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--text-muted) !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3) !important;
}
html.dark .el-popover__title {
  color: var(--text-main) !important;
}

/* 17. 全局 checkbox 多选框暗色模式覆盖 */
html.dark .el-checkbox__inner {
  background-color: var(--bg-page) !important;
  border-color: var(--border-color) !important;
}
html.dark .el-checkbox__inner::after {
  border-color: #ffffff !important;
}
html.dark .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}
html.dark .el-checkbox__input.is-disabled .el-checkbox__inner {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-color) !important;
}
html.dark .el-checkbox__label {
  color: var(--text-muted) !important;
}
html.dark .el-checkbox__input.is-checked + .el-checkbox__label {
  color: var(--text-main) !important;
}

/* 18. 全局 el-button plain (朴素按钮) 及文字按钮样式全局覆盖 */
html.dark .el-button--primary.is-plain {
  background-color: rgba(59, 130, 246, 0.15) !important;
  border-color: rgba(59, 130, 246, 0.3) !important;
  color: #60a5fa !important;
}
html.dark .el-button--primary.is-plain:hover {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: #ffffff !important;
}
html.dark .el-button--success.is-plain {
  background-color: rgba(16, 185, 129, 0.15) !important;
  border-color: rgba(16, 185, 129, 0.3) !important;
  color: #34d399 !important;
}
html.dark .el-button--success.is-plain:hover {
  background-color: #10b981 !important;
  border-color: #10b981 !important;
  color: #ffffff !important;
}
html.dark .el-button--warning.is-plain {
  background-color: rgba(245, 158, 11, 0.15) !important;
  border-color: rgba(245, 158, 11, 0.3) !important;
  color: #fbbf24 !important;
}
html.dark .el-button--warning.is-plain:hover {
  background-color: #f59e0b !important;
  border-color: #f59e0b !important;
  color: #ffffff !important;
}
html.dark .el-button--danger.is-plain {
  background-color: rgba(239, 68, 68, 0.15) !important;
  border-color: rgba(239, 68, 68, 0.3) !important;
  color: #f87171 !important;
}
html.dark .el-button--danger.is-plain:hover {
  background-color: #ef4444 !important;
  border-color: #ef4444 !important;
  color: #ffffff !important;
}
html.dark .el-button--info.is-plain {
  background-color: rgba(100, 116, 139, 0.15) !important;
  border-color: rgba(100, 116, 139, 0.3) !important;
  color: #94a3b8 !important;
}
html.dark .el-button--info.is-plain:hover {
  background-color: #64748b !important;
  border-color: #64748b !important;
  color: #ffffff !important;
}
html.dark .el-button--text {
  color: #60a5fa !important;
}
html.dark .el-button--text:hover {
  color: #93c5fd !important;
}
html.dark .el-button--text.is-disabled {
  color: #475569 !important;
}

/* 19. 表格高亮行与分割线细节精修（防止白边、右边框白色列缝、固定列滚动白条） */
html.dark .el-table td.el-table__cell,
html.dark .el-table th.el-table__cell {
  border-bottom: 1px solid var(--border-light) !important;
  border-right: 1px solid var(--border-light) !important;
}
html.dark .el-table--border th.el-table__cell,
html.dark .el-table__fixed-right-patch {
  border-bottom: 1px solid var(--border-color) !important;
  border-right: 1px solid var(--border-color) !important;
  background-color: var(--bg-page) !important;
}
html.dark .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell,
html.dark .el-table__body tr.hover-row > td.el-table__cell,
html.dark .el-table__body tr.hover-row.current-row > td.el-table__cell,
html.dark .el-table__body tr.hover-row.el-table__row--striped > td.el-table__cell {
  background-color: var(--bg-hover) !important;
}
html.dark .el-table__fixed {
  box-shadow: none !important;
}
html.dark .el-table__fixed-right {
  box-shadow: none !important;
}

/* 20. 全局抽屉及表单在暗色模式下的强力覆盖 */
html.dark .el-drawer,
html.dark .rest-form-drawer,
html.dark .mch-group-drawer,
html.dark .product-form-drawer,
html.dark .xs-channel-drawer,
html.dark .logic-args-drawer {
  background-color: var(--bg-page) !important;
}
html.dark .el-drawer__body {
  background-color: var(--bg-page) !important;
}
html.dark .drawer-header,
html.dark .breadcrumb-header {
  background-color: var(--bg-card) !important;
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .drawer-title,
html.dark .header-title {
  color: var(--text-main) !important;
}
html.dark .drawer-close-btn,
html.dark .form-drawer-close {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
}
html.dark .drawer-content,
html.dark .detail-content,
html.dark .form-drawer-content {
  background-color: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
}
html.dark .drawer-footer,
html.dark .form-drawer-footer {
  background-color: var(--bg-card) !important;
  border-top: 1px solid var(--border-color) !important;
}
html.dark .detail-workspace,
html.dark .rest-workspace,
html.dark .is-drawer-workspace {
  background-color: var(--bg-page) !important;
}
html.dark .is-drawer-workspace .breadcrumb-header {
  background-color: var(--bg-card) !important;
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .panel-section-title {
  color: var(--text-main) !important;
}
html.dark .section-subtitle {
  color: var(--text-muted) !important;
}
html.dark .form-section,
html.dark .panel-section-header {
  border-bottom: 1px solid var(--border-color) !important;
}
html.dark .drawer-horizontal-form :deep(.el-form-item__label),
html.dark .form-container :deep(.el-form-item__label),
html.dark .el-form-item__label {
  color: var(--text-muted) !important;
}
html.dark .btn-add-row {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-color) !important;
  color: var(--primary-color) !important;
}
html.dark .btn-add-row:hover {
  background-color: var(--bg-hover-deep) !important;
  border-color: var(--border-hover) !important;
}
html.dark .mch-table-container {
  border-color: var(--border-color) !important;
}
html.dark .inline-edit-table :deep(.el-input__inner) {
  border-color: var(--border-color) !important;
  background-color: var(--bg-page) !important;
  color: var(--text-main) !important;
}

/* 21. 表格固定列全容器及表头 Patch 强力暗色覆盖 */
html.dark .el-table__fixed-right,
html.dark .el-table__fixed,
html.dark .el-table__fixed-right-patch,
html.dark .el-table__fixed-header-wrapper,
html.dark .el-table__fixed-body-wrapper,
html.dark .el-table__fixed-right .el-table__cell,
html.dark .el-table__fixed .el-table__cell {
  background-color: var(--bg-card) !important;
}
html.dark .el-table__fixed-right-patch {
  background-color: var(--bg-page) !important;
  border-bottom-color: var(--border-color) !important;
}

/* 22. 表格内部操作按钮暗色模式美化 */
html.dark .el-table .el-button.is-plain {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}
html.dark .el-table .el-button--primary.is-plain {
  color: #60a5fa !important;
  border-color: rgba(59, 130, 246, 0.25) !important;
}
html.dark .el-table .el-button--primary.is-plain:hover {
  background-color: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
}
html.dark .el-table .el-button--danger.is-plain {
  color: #f87171 !important;
  border-color: rgba(239, 68, 68, 0.25) !important;
}
html.dark .el-table .el-button--danger.is-plain:hover {
  background-color: #ef4444 !important;
  border-color: #ef4444 !important;
  color: #ffffff !important;
}
html.dark .el-table .el-button--success.is-plain {
  color: #34d399 !important;
  border-color: rgba(16, 185, 129, 0.25) !important;
}
html.dark .el-table .el-button--success.is-plain:hover {
  background-color: #10b981 !important;
  border-color: #10b981 !important;
  color: #ffffff !important;
}
html.dark .el-table .el-button--warning.is-plain {
  color: #fbbf24 !important;
  border-color: rgba(245, 158, 11, 0.25) !important;
}
html.dark .el-table .el-button--warning.is-plain:hover {
  background-color: #f59e0b !important;
  border-color: #f59e0b !important;
  color: #ffffff !important;
}
html.dark .el-table .el-button--text {
  color: #60a5fa !important;
}
html.dark .el-table .el-button--text:hover {
  color: #93c5fd !important;
}

/* 23. 全局表格固定列与普通列在各种状态（常规、斑马纹、hover）下的强力背景色与悬浮色一致性同步 */
/* 消除投影 */
.el-table__fixed,
.el-table__fixed-right,
html.dark .el-table__fixed,
html.dark .el-table__fixed-right {
  box-shadow: none !important;
}

/* 亮色模式：常规固定列同步 */
.el-table__fixed .el-table__cell,
.el-table__fixed-right .el-table__cell,
.premium-table .el-table__fixed .el-table__cell,
.premium-table .el-table__fixed-right .el-table__cell {
  background-color: var(--bg-card) !important;
}

/* 亮色模式：斑马纹行固定列同步 */
.el-table--striped .el-table__row--striped td,
.el-table--striped .el-table__row--striped .el-table__cell,
.premium-table.el-table--striped .el-table__row--striped td,
.premium-table.el-table--striped .el-table__row--striped .el-table__cell {
  background-color: var(--bg-page) !important;
}

/* 亮色模式：悬浮 hover 固定列同步 */
.el-table__body tr:hover > td,
.el-table__body tr:hover > td.el-table__cell,
.el-table__fixed .el-table__body tr:hover > td,
.el-table__fixed .el-table__body tr:hover > td.el-table__cell,
.el-table__fixed-right .el-table__body tr:hover > td,
.el-table__fixed-right .el-table__body tr:hover > td.el-table__cell,
.el-table__body tr.hover-row > td,
.el-table__body tr.hover-row > td.el-table__cell,
.el-table__fixed .el-table__body tr.hover-row > td,
.el-table__fixed .el-table__body tr.hover-row > td.el-table__cell,
.el-table__fixed-right .el-table__body tr.hover-row > td,
.el-table__fixed-right .el-table__body tr.hover-row > td.el-table__cell,
.premium-table .el-table__body tr:hover > td,
.premium-table .el-table__fixed .el-table__body tr:hover > td,
.premium-table .el-table__fixed-right .el-table__body tr:hover > td,
.premium-table .el-table__body tr.hover-row > td,
.premium-table .el-table__fixed .el-table__body tr.hover-row > td,
.premium-table .el-table__fixed-right .el-table__body tr.hover-row > td {
  background-color: var(--bg-hover) !important;
}

/* 深色模式：常规固定列同步 */
html.dark .el-table__fixed .el-table__cell,
html.dark .el-table__fixed-right .el-table__cell,
html.dark .premium-table .el-table__fixed .el-table__cell,
html.dark .premium-table .el-table__fixed-right .el-table__cell {
  background-color: var(--bg-card) !important;
}

/* 深色模式：斑马纹行固定列同步 */
html.dark .el-table--striped .el-table__row--striped td,
html.dark .el-table--striped .el-table__row--striped .el-table__cell,
html.dark .premium-table.el-table--striped .el-table__row--striped td,
html.dark .premium-table.el-table--striped .el-table__row--striped .el-table__cell {
  background-color: rgba(15, 23, 42, 0.45) !important;
}

/* 深色模式：悬浮 hover 固定列同步 */
html.dark .el-table__body tr:hover > td,
html.dark .el-table__body tr:hover > td.el-table__cell,
html.dark .el-table__fixed .el-table__body tr:hover > td,
html.dark .el-table__fixed .el-table__body tr:hover > td.el-table__cell,
html.dark .el-table__fixed-right .el-table__body tr:hover > td,
html.dark .el-table__fixed-right .el-table__body tr:hover > td.el-table__cell,
html.dark .el-table__body tr.hover-row > td,
html.dark .el-table__body tr.hover-row > td.el-table__cell,
html.dark .el-table__fixed .el-table__body tr.hover-row > td,
html.dark .el-table__fixed .el-table__body tr.hover-row > td.el-table__cell,
html.dark .el-table__fixed-right .el-table__body tr.hover-row > td,
html.dark .el-table__fixed-right .el-table__body tr.hover-row > td.el-table__cell,
html.dark .premium-table .el-table__body tr:hover > td,
html.dark .premium-table .el-table__fixed .el-table__body tr:hover > td,
html.dark .premium-table .el-table__fixed-right .el-table__body tr:hover > td,
html.dark .premium-table .el-table__body tr.hover-row > td,
html.dark .premium-table .el-table__fixed .el-table__body tr.hover-row > td,
html.dark .premium-table .el-table__fixed-right .el-table__body tr.hover-row > td {
  background-color: var(--bg-hover) !important;
}

/* 修复表格加载中、空数据、错误或无权限等非正常数据展示状态下，固定列（如操作列等）穿透遮罩层露出空白块或位置闪烁的问题 */
.table-content-area.has-state .el-table .el-table__fixed,
.table-content-area.has-state .el-table .el-table__fixed-right {
  opacity: 0 !important;
  pointer-events: none !important;
  transition: opacity 0.15s ease;
}

/* 统一重载按钮在深色模式下的底色和悬浮样式，防止被 el-button--warning 强行覆盖 */
html.dark .btn-reload {
  background-color: var(--bg-reload) !important;
  border-color: var(--border-reload) !important;
  color: var(--text-reload) !important;
}
html.dark .btn-reload:hover,
html.dark .btn-reload:focus {
  background-color: var(--bg-reload-hover) !important;
  border-color: var(--border-reload-hover) !important;
  color: var(--text-reload-hover) !important;
}

/* ==========================================================================
   全站控制交互组件与静态只读元素的防选择、防误显光标规范，提升原生系统质感
   ========================================================================== */
.el-button,
.el-breadcrumb,
.el-tabs__item,
.el-dialog__header,
.el-dialog__footer,
.el-dropdown-menu,
.el-pagination,
.el-step,
.el-table th,
.table-toolbar,
.sidebar-collapse-trigger,
.logo-wrapper,
i,
.svg-inline--fa,
span.premium-header-title,
.cell-id,
.cell-text-gray,
.cell-text-main,
.cell-rate,
.cell-platform,
.status-dot-badge,
.status-simple-badge {
  user-select: none !important;
}

/* 1. 可点击/交互类型组件：鼠标悬停显示手型指针 */
.el-button,
.el-tabs__item,
.el-dropdown-menu__item,
.sidebar-collapse-trigger,
.logo-wrapper,
span.premium-header-title {
  cursor: pointer !important;
}

/* 2. 纯只读/控制类型静态内容：鼠标悬停展示默认指示箭头 */
.el-breadcrumb,
.el-dialog__header,
.el-dialog__footer,
.el-pagination,
.el-table th,
.cell-id,
.cell-text-gray,
.cell-text-main,
.cell-rate,
.cell-platform,
.status-dot-badge,
.status-simple-badge {
}

/* ==========================================================================
   全局通用精致状态徽标 (Status Dot Badge) 样式
   ========================================================================== */
.status-dot-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  box-sizing: border-box;
}

.status-dot-badge .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot-badge.online {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.08);
}
.status-dot-badge.online .dot {
  background-color: #10b981;
}

.status-dot-badge.testing {
  color: #d97706;
  background-color: rgba(245, 158, 11, 0.08);
}
.status-dot-badge.testing .dot {
  background-color: #f59e0b;
}

.status-dot-badge.developing {
  color: var(--primary-color);
  background-color: rgba(37, 99, 235, 0.08);
}
.status-dot-badge.developing .dot {
  background-color: var(--primary-color);
}

.status-dot-badge.closed {
  color: #dc2626;
  background-color: rgba(239, 68, 68, 0.08);
}
.status-dot-badge.closed .dot {
  background-color: #ef4444;
}

/* ==========================================================================
   全局通用极简状态开关徽标 (Status Simple Badge) 样式美化
   覆盖游戏管理（实名、防沉迷、UFW、Pluto、手机绑定等）、XS游戏、商户管理等开关字段
   ========================================================================== */
.status-simple-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 20px;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.status-simple-badge .dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.status-simple-badge.active {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.2);
}
.status-simple-badge.active .dot {
  background-color: #10b981;
}

.status-simple-badge.inactive {
  color: #64748b;
  background-color: rgba(100, 116, 139, 0.08);
  border: 1px solid rgba(100, 116, 139, 0.15);
}
.status-simple-badge.inactive .dot {
  background-color: #94a3b8;
}

/* 深色模式下的高质感微光样式 */
html.dark .status-simple-badge.active {
  color: #34d399 !important;
  background-color: rgba(16, 185, 129, 0.15) !important;
  border: 1px solid rgba(16, 185, 129, 0.3) !important;
}
html.dark .status-simple-badge.active .dot {
  background-color: #34d399 !important;
  box-shadow: 0 0 6px rgba(52, 211, 153, 0.6) !important;
}

html.dark .status-simple-badge.inactive {
  color: #94a3b8 !important;
  background-color: rgba(148, 163, 184, 0.12) !important;
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
html.dark .status-simple-badge.inactive .dot {
  background-color: #64748b !important;
}

/* ==========================================================================
   Ext 游戏与 Ext 渠道 Popover 弹窗、Alert 提示框及 Warning Tag 强力暗色覆盖
   解决深色模式下 Popover 悬浮说明文字发黑、底色刺眼、Tag 文字不可看清的 Bug
   ========================================================================== */
html.dark .el-tag--warning,
html.dark .ext-template-tag {
  color: #fbbf24 !important;
  background-color: rgba(245, 158, 11, 0.16) !important;
  border-color: rgba(245, 158, 11, 0.35) !important;
  font-weight: 600 !important;
}

/* 挂载在 body 上的 Popover 与内部 Alert 全局暗色高亮美化 */
html.dark .el-popover {
  background-color: #1e293b !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5) !important;
  color: #f1f5f9 !important;
}

html.dark .el-popper[x-placement^=top] .popper__arrow::after {
  border-top-color: #1e293b !important;
}

html.dark .el-popper[x-placement^=bottom] .popper__arrow::after {
  border-bottom-color: #1e293b !important;
}

html.dark .el-popover .el-alert--warning,
html.dark .el-popover .ext-template-alert,
html.dark .ext-template-alert {
  background-color: rgba(245, 158, 11, 0.12) !important;
  border: 1px solid rgba(245, 158, 11, 0.3) !important;
  border-radius: 8px !important;
}

html.dark .el-popover .el-alert--warning .el-alert__title,
html.dark .el-popover .el-alert--warning .el-alert__description,
html.dark .ext-template-alert .el-alert__title,
html.dark .ext-template-alert .el-alert__description {
  color: #fef08a !important; /* 高亮明亮金黄文本，高对比度防发黑 */
  font-size: 13px !important;
  line-height: 1.5 !important;
  font-weight: 500 !important;
}
</style>
